/**
 * HM Icon Library Constants
 *
 * Type-safe constants for Happy Icons font library
 * Total icons available: 636
 *
 * Usage:
 * ```tsx
 * import { ICONS } from '@/constants/icons';
 * <i className={`hm hm-${ICONS.DASHBOARD}`} />
 * ```
 */

import { devLog } from '@/utils/logger';

/**
 * Commonly used icons organized by category
 */
export const ICONS = {
  // ===========================
  // NAVIGATION & UI
  // ===========================
  DASHBOARD: 'dashboard',
  CALENDAR: 'calendar',
  MENU: 'fly-out-menu',
  HAMBURGER: 'navigation1',
  CLOSE: 'cross',
  HOME: 'home',
  SEARCH: 'search',
  FILTER: 'filter',
  SETTINGS: 'tools',
  COG: 'cog',

  // ===========================
  // ARROWS & DIRECTIONS
  // ===========================
  ARROW_LEFT: 'arrow-left',
  ARROW_RIGHT: 'arrow-right',
  ARROW_UP: 'arrow-up',
  ARROW_DOWN: 'arrow-down',
  ANGLE_LEFT: 'angle-left',
  ANGLE_RIGHT: 'angle-right',
  ANGLE_UP: 'angle-up',
  ANGLE_DOWN: 'angle-down',
  CARET_LEFT: 'caret-left',
  CARET_RIGHT: 'caret-right',
  CARET_UP: 'caret-up',
  CARET_DOWN: 'caret-down',
  CHEVRON_DOWN: 'caret-down',
  CHEVRON_UP: 'caret-up',
  CHEVRON_LEFT: 'caret-left',
  CHEVRON_RIGHT: 'caret-right',

  // ===========================
  // ACTIONS & INTERACTIONS
  // ===========================
  PLUS: 'plus-gap',
  PLUS_CIRCLE: 'plus-circle',
  MINUS: 'minus-small',
  EDIT: 'pencil',
  DRAFT: 'pen-pencil',
  DELETE: 'cross-circle',
  TRASH: 'cross-circle',
  // Real trash-can glyph (vs. TRASH's cross-circle, used for "delete this
  // row" actions) — for states that mean "this thing was thrown away
  // elsewhere", e.g. a blog's WordPress post trashed on the WP side.
  RECYCLE_BIN: 'recycle-bin',
  SAVE: 'disc',
  COPY: 'copy',
  HISTORY: 'timeline',
  DOWNLOAD: 'download',
  UPLOAD: 'upload',
  REFRESH: 'refresh',
  UNDO: 'refresh',
  REDO: 'refresh2',
  SHARE: 'share',
  LINK: 'link',
  EXTERNAL_LINK: 'arrow-corner',
  CROSS: 'cross',
  TICK_CIRCLE: 'tick-circle',
  PAPER_PLANE: 'paper-plane',
  ELLIPSIS_VERTICAL: 'ellipsis-vertical',
  SPARK: 'spark',
  PULSE: 'pulse',
  PEN: 'pen',

  // ===========================
  // CONTENT & MEDIA
  // ===========================
  IMAGE: 'image',
  VIDEO: 'slider-video',
  VIDEO_CAMERA: 'video-camera',
  CAMERA: 'camera',
  FILE: 'file',
  ATTACH: 'clip',
  FOLDER: 'folder',
  DOCUMENT: 'file',
  GALLERY: 'image',
  PICTURE: 'image',
  BLOG: 'blog-content',
  EMO_SMILE: 'emo-smile',
  GRID: 'grid',
  LIST: 'list',
  PLAY: 'play',
  PLAY_BUTTON: 'play-button',

  // ===========================
  // COMMUNICATION
  // ===========================
  MAIL: 'mail-open',
  ENVELOPE: 'mail-open',
  MESSAGE: 'chat-bubble',
  CHAT: 'chat-bubble',
  NOTIFICATION: 'bell',
  BELL: 'bell',

  // ===========================
  // SOCIAL MEDIA PLATFORMS
  // Note: Some platforms don't have dedicated icons in HappyIcons,
  // using closest alternatives or generic icons
  // ===========================
  FACEBOOK: 'facebook',
  TWITTER: 'twitter',
  LINKEDIN: 'linkedin',
  INSTAGRAM: 'instagram',
  YOUTUBE: 'youtube',
  PINTEREST: 'share', // No pinterest icon, using share
  TIKTOK: 'play', // No tiktok icon, using play
  THREADS: 'chat-bubble', // Threads - using chat
  WHATSAPP: 'chat-bubble', // No whatsapp icon, using chat
  TELEGRAM: 'paper-plane', // No telegram icon, using paper-plane
  CLOUD: 'cloud',
  BLUESKY: 'cloud', // Bluesky - using cloud
  GMB: 'location-pointer', // Google My Business
  ARCHIVE: 'file-cabinet', // Archived posts

  // ===========================
  // USER & PROFILE
  // ===========================
  USER: 'user',
  USER_CIRCLE: 'user',
  USERS: 'group',
  TEAM: 'group',
  AVATAR_MAN: 'avatar-man',
  AVATAR_WOMAN: 'avatar-woman',
  PROFILE: 'user',
  LOGIN: 'sign-in',
  LOGOUT: 'sign-out',

  // ===========================
  // STATUS & FEEDBACK
  // ===========================
  CHECK: 'tick',
  CHECK_CIRCLE: 'tick-circle',
  RELEASE_STAMP: 'release-stamp', // Same outlined approval stamp used by the Release Drawer
  CROSS_CIRCLE: 'cross-circle',
  INFO: 'info',
  WARNING: 'caution',
  CAUTION: 'caution',
  ERROR: 'cross-circle',
  SUCCESS: 'tick-circle',
  LOADER: 'sync',
  SPINNER: 'sync',
  // Occurrence status badges (Stage 2c-i, post-status reconciliation)
  PARTIAL: 'graph-pie2', // Partial success — half-pie, distinct from FAILED's caution icon
  MISSED: 'sand-watch', // Missed (overdue, never attempted) — hourglass ran out

  // ===========================
  // AI & MAGIC (Agile Assets Specific)
  // ===========================
  MAGIC_WAND: 'magic-wand',
  RADAR: 'radar', // Brand Check
  ROBOT: 'brain',
  SPARKLES: 'spark',
  BULB: 'bulb',

  // ===========================
  // BUSINESS & ANALYTICS
  // ===========================
  CHART: 'graph',
  GRAPH: 'graph',
  STATS: 'graph',
  ANALYTICS: 'speedometer',
  TREND_UP: 'arrow-up',
  TREND_DOWN: 'arrow-down',

  // ===========================
  // DASHBOARD & STATS
  // ===========================
  ROCKET: 'rocket1',
  SEND: 'plane',
  REMIX: 'repeat',
  ALERT_CIRCLE: 'caution',
  TARGET: 'target',

  // ===========================
  // TIME & SCHEDULING
  // ===========================
  CLOCK: 'alarm-clock',
  ALARM_CLOCK: 'alarm-clock',
  TIME: 'alarm-clock',
  SCHEDULE: 'calendar',

  // ===========================
  // TOOLS & UTILITIES
  // ===========================
  TOOLS: 'tools',
  WRENCH: 'tools',
  HAMMER: 'tools',
  PAINT: 'brush-paint',
  COLOR: 'color-plate',
  CROP: 'crop',

  // ===========================
  // AUDIO & MEDIA
  // ===========================
  SOUND: 'speaker-on',
  SPEAKER: 'speaker-on',
  SPEAKER_OFF: 'speaker-off',
  HEADPHONE: 'headphone',
  MUSIC: 'headphone',
  COFFEE: 'cup-coffee',
  LEAF: 'tree-square',

  // ===========================
  // ECOMMERCE & PAYMENT
  // ===========================
  CART: 'cart-full',
  CART_EMPTY: 'cart-empty',
  CREDIT_CARD: 'card',
  WALLET: 'wallet',
  TAG: 'tag',
  CAROUSEL: 'carousel',
  LAYER: 'layer',
  TEXT_OUTLINE: 'text-outline',
  CALENDAR_WEEK: 'calendar2',
  PRICE: 'tag',

  // ===========================
  // LOCATION & MAP
  // ===========================
  LOCATION: 'location-pointer',
  MAP: 'map',
  PIN: 'pin',
  MAP_PIN: 'map-pointer',
  MAP_POINTER: 'map-pointer',
  COMPASS: 'compass',
  GLOBE: 'globe1',
  WORLD: 'globe3',
  LANGUAGE: 'language-change',

  // ===========================
  // SECURITY & LOCK
  // ===========================
  LOCK: 'lock',
  UNLOCK: 'unlock',
  KEY: 'key',
  SHIELD: 'shield',
  // Note: HappyIcons doesn't have eye icons, using reading-glass as alternative
  EYE: 'reading-glass',
  EYE_SLASH: 'reading-glass-alt',
  REVIEW: 'review',

  // ===========================
  // DRAG & DROP
  // ===========================
  GRIP: 'fly-out-menu', // Using menu icon as grip/drag handle
  DRAG: 'drag',
  MOVE: 'drag',
  HAND: 'hand',

  // ===========================
  // MISCELLANEOUS
  // ===========================
  STAR: 'star',
  WEATHER_NIGHT_CLOUD: 'weather-night-cloud',
  HEART: 'heart',
  THUMBS_UP: 'like',
  THUMBS_DOWN: 'dislike',
  FLAG: 'flag',
  BOOKMARK: 'star',
  CROWN: 'crown',
  TROPHY: 'cup',
  GIFT: 'gift-box',
} as const;

/**
 * Type-safe icon name
 * Allows both predefined constants and any string for flexibility
 */
export type IconName = (typeof ICONS)[keyof typeof ICONS] | string;

/**
 * Helper function to get full icon class name
 *
 * @param iconName - Icon name (with or without 'hm-' prefix)
 * @returns Full className string
 *
 * @example
 * getIconClass(ICONS.DASHBOARD) // 'hm hm-dashboard'
 * getIconClass('dashboard') // 'hm hm-dashboard'
 * getIconClass('hm-dashboard') // 'hm hm-dashboard'
 */
export const getIconClass = (iconName: string | undefined): string => {
  // Handle undefined/null/empty icon names
  if (!iconName) {
    devLog.warn('getIconClass: undefined icon name provided, using fallback');
    return 'hm hm-question-circle'; // Fallback icon
  }

  // Remove 'hm-' prefix if present
  const cleanName = iconName.replace(/^hm-/, '');
  return `hm hm-${cleanName}`;
};

/**
 * Social media platform icon mapping
 * For use in platform selectors, channel lists, etc.
 */
export const PLATFORM_ICONS: Record<string, string> = {
  facebook: ICONS.FACEBOOK,
  twitter: ICONS.TWITTER,
  linkedin: ICONS.LINKEDIN,
  instagram: ICONS.INSTAGRAM,
  youtube: ICONS.YOUTUBE,
  pinterest: ICONS.PINTEREST,
  tiktok: ICONS.TIKTOK,
  threads: ICONS.THREADS,
  bluesky: ICONS.BLUESKY,
  google_my_business: ICONS.GLOBE,
  whatsapp: ICONS.WHATSAPP,
  telegram: ICONS.TELEGRAM,
};

/**
 * Navigation icon mapping
 * For use in sidebar, bottom nav, etc.
 */
export const NAV_ICONS = {
  dashboard: ICONS.DASHBOARD,
  calendar: ICONS.CALENDAR,
  creator: ICONS.MAGIC_WAND,
  brandCheck: ICONS.RADAR,
  settings: ICONS.TOOLS,
} as const;

/**
 * Status icon mapping
 * For use in status badges, notifications, etc.
 */
export const STATUS_ICONS = {
  success: ICONS.CHECK_CIRCLE,
  error: ICONS.CROSS_CIRCLE,
  warning: ICONS.WARNING,
  info: ICONS.INFO,
  pending: ICONS.CLOCK,
  loading: ICONS.LOADER,
} as const;
